import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import numberToWords from "number-to-words";
import "./GrantForm.css";
import GoldValuationModal from "./GoldValuationModal";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";
import useFormDraft from "../hooks/useFormDraft";

const DRAFT_KEY = "grantFormDraft";
const TXN_SHARE_KEY = "grantFormTransactionDraft";

const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Pawn expiry rule:
// - Normally: same calendar date 6 months later, minus 1 day.
// - If that day does not exist in the target month (e.g. 29/30/31 Aug -> Feb),
//   expire on the LAST day of the target month instead of overflowing into March.
const calculatePawnExpiry = (grantDate) => {
  const year = grantDate.getFullYear();
  const month = grantDate.getMonth();
  const day = grantDate.getDate();

  const targetMonthIndex = month + 6;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = targetMonthIndex % 12;
  const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();

  if (day > lastDayOfTargetMonth) {
    return new Date(targetYear, targetMonth, lastDayOfTargetMonth);
  }

  return new Date(targetYear, targetMonth, day - 1);
};

const convertToISO = (dateStr) => {
  const parts = (dateStr || "").split("-");
  if (parts.length !== 3) return "";
  const month = new Date(Date.parse(parts[1] + " 1, 2000")).getMonth() + 1;
  return `${parts[2]}-${String(month).padStart(2, "0")}-${parts[0]}T00:00:00`;
};

const buildDefaultDates = () => {
  const today = new Date();
  const expiry = calculatePawnExpiry(today);

  return {
    grantDate: formatDate(today),
    expiryDate: formatDate(expiry),
  };
};

// ===== NEW HELPERS =====
const sanitizeNumber = (s = "") => (s || "").replace(/,/g, "").trim();

/**
 * Count items by summing quantities found in free text.
 * Supports:
 * • “3N” / “3 N” → +3
 * • bare “1 GOLD RING” → +1 (skips purity like 916/750 and units like G/CT/K)
 * Falls back to comma-split count if text present but no explicit quantities.
 */
const countItems = (text = "") => {
  const src = (text || "").toUpperCase();
  let total = 0;

  // 1) "3N" or "3 N"
  const nTok = /\b(\d+)\s*N\b/g;
  let m;
  while ((m = nTok.exec(src))) total += parseInt(m[1], 10);

  // 2) Bare counts: "1 GOLD RING"
  //    number followed by a word that's not a unit (G/GM/CT/K/etc.)
  const bare = /\b(\d+)\s+(?!G(?:M|MS)?\b|GRAMS?\b|KG\b|OZ\b|CT\b|CARAT\S*\b|K\b|KT\b)\w+/g;
  const skipPurity = new Set([999, 916, 905, 900, 875, 750, 585, 417, 375]);
  while ((m = bare.exec(src))) {
    const n = parseInt(m[1], 10);
    if (skipPurity.has(n)) continue;
    total += n;
  }

  // 3) Fallback to comma-separated item count if nothing matched but text exists
  if (total === 0 && src.trim().length > 0) {
    total = src
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean).length;
  }
  return total;
};

// ===== DRAFT CLEANUP =====
// Backward compatibility: strip out old date fields from existing drafts
// so useFormDraft doesn't pull in stale dates.
try {
  const existingDraft = localStorage.getItem(DRAFT_KEY);
  if (existingDraft) {
    const parsed = JSON.parse(existingDraft);
    if (parsed.grantDate || parsed.expiryDate) {
      delete parsed.grantDate;
      delete parsed.expiryDate;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(parsed));
    }
  }
} catch (e) {
  // If JSON is malformed for any reason, wipe it to prevent crashes
  localStorage.removeItem(DRAFT_KEY);
}

export default function GrantForm() {
  useEscapeToGoBack();
  const navigate = useNavigate();

  // draft (localStorage)
  const [draft, setDraft] = useFormDraft(DRAFT_KEY, {
    itemsInput: "",
    principal: "",
    valuation: "",
    weight: "",
    remark: "",
    staffCode: "",
  });

  // lookups for Tab-expand
  const [itemMap, setItemMap] = useState({});
  const [materialMap, setMaterialMap] = useState({});
  const [conditionMap, setConditionMap] = useState({});

  // form state
  const [itemsInput, setItemsInput] = useState(draft.itemsInput); // free text
  const [principal, setPrincipal] = useState(draft.principal);
  const [valuation, setValuation] = useState(draft.valuation);
  const [weight, setWeight] = useState(draft.weight);
  const [remark, setRemark] = useState(draft.remark);
  const [grantDate, setGrantDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [staffCode, setStaffCode] = useState(draft.staffCode);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // single textarea ref for Tab-expand
  const itemsRef = useRef(null);

  // keep draft in sync
  useEffect(() => {
    setDraft({
      itemsInput,
      principal,
      valuation,
      weight,
      remark,
      staffCode,
    });
  }, [itemsInput, principal, valuation, weight, remark, staffCode, setDraft]);

  // init lists/session/dates once
  useEffect(() => {
    axios
      .get("https://www.lianfoong.com/api/items/getAllItems")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((it) => {
          if (it?.code) map[it.code.toLowerCase()] = it.itemDesc || it.code;
        });
        setItemMap(map);
      })
      .catch(() => {});

    axios
      .get("https://www.lianfoong.com/api/materials/getAllMaterials")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((m) => {
          if (m?.code) map[m.code.toLowerCase()] = m.material || m.code;
        });
        setMaterialMap(map);
      })
      .catch(() => {});

    axios
      .get("https://www.lianfoong.com/api/item-conditions/getAllItemConditions")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((c) => {
          if (c?.code) map[c.code.toLowerCase()] = c.itemCondition || c.code;
        });
        setConditionMap(map);
      })
      .catch(() => {});

    axios
      .get("https://www.lianfoong.com/api/staff/session", { withCredentials: true })
      .then((res) => {
        if (res.data?.staffCode) setStaffCode(res.data.staffCode);
      })
      .catch(() => {});

    // Backward compatibility: migrate old draft.items (array) → itemsInput
    if (!draft.itemsInput && Array.isArray(draft.items) && draft.items.length) {
      const migrated = draft.items.map((i) => i?.value || "").filter(Boolean).join(", ");
      setItemsInput(migrated);
    }

    const { grantDate: freshGrantDate, expiryDate: freshExpiryDate } = buildDefaultDates();
    setGrantDate(freshGrantDate);
    setExpiryDate(freshExpiryDate);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab-to-expand inside the single textarea
  const handleItemsKeyDown = (e) => {
    if (e.key !== "Tab" || e.shiftKey) return;
    const textarea = itemsRef.current;
    if (!textarea) return;

    const value = itemsInput || "";
    const cursor = textarea.selectionStart ?? value.length;

    // Token = word under cursor (split by spaces or commas)
    const left = value.lastIndexOf(" ", Math.max(0, cursor - 1));
    const leftComma = value.lastIndexOf(",", Math.max(0, cursor - 1));
    const start = Math.max(left, leftComma) + 1;

    const nextSpace = value.indexOf(" ", cursor);
    const nextComma = value.indexOf(",", cursor);
    const r = [nextSpace, nextComma].filter((x) => x !== -1);
    const end = r.length ? Math.min(...r) : value.length;

    const token = value.slice(start, end).trim();
    if (!token) return;

    const key = token.toLowerCase();
    const hit = itemMap[key] ?? materialMap[key] ?? conditionMap[key];
    if (!hit) return;

    e.preventDefault();
    const replacement = (hit || token).toUpperCase();
    const newValue = value.slice(0, start) + replacement + value.slice(end);
    setItemsInput(newValue);

    requestAnimationFrame(() => {
      const pos = start + replacement.length;
      textarea.setSelectionRange(pos, pos);
      textarea.focus();
    });
  };

  const convertToWords = (num) => {
    const clean = sanitizeNumber(num);
    return !clean || isNaN(Number(clean))
      ? ""
      : numberToWords.toWords(Number(clean)).toUpperCase() + " DOLLARS ONLY";
  };

  // NEW: quantity-based count
  const itemsCount = countItems(itemsInput);

  const handleSubmit = () => {
    const tx = {
      items: (itemsInput || "").trim(),             // keep free text as-is
      weight: sanitizeNumber(weight),
      principal: sanitizeNumber(principal),         // strip commas
      valuation: sanitizeNumber(valuation),         // strip commas
      remark,
      noOfItem: itemsCount,
      grantDate: convertToISO(grantDate),
      expiryDate: convertToISO(expiryDate),
      handler: staffCode,
    };
    try {
      localStorage.setItem(TXN_SHARE_KEY, JSON.stringify(tx));
    } catch {}
    navigate("/pawner-form", { state: { transactionData: tx } });
  };

  return (
    <div className="grf-page grf-nosroll">
      <div className="grant-form grant-form--wide">
        {/* Header */}
        <div className="grf-header">
          <h2 className="grf-title">Grant Form</h2>
          <div className="grf-meta">
            <span className="grf-badge">Items: {itemsCount}</span>
          </div>
        </div>

        {/* Body — wide, no internal scrolling */}
        <div className="grf-body-noscroll">
          {/* Single items textarea (free text) */}
          <div className="grf-section-title"></div>
          <div className="grf-item-single">
            <label className="grf-label">Items</label>
            <textarea
              ref={itemsRef}
              className="grf-input grf-textarea"
              value={itemsInput}
              onChange={(e) => setItemsInput((e.target.value || "").toUpperCase())}
              onKeyDown={handleItemsKeyDown}
              rows={4}
            />
          </div>

          {/* Fields laid across 12 columns */}
          <div className="grf-grid-12">
            <div className="grf-field col-6">
              <label className="grf-label">Remark</label>
              <input
                type="text"
                className="grf-input"
                value={remark}
                onChange={(e) => setRemark((e.target.value || "").toUpperCase())}
              />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Amount</label>
              <div className="grf-stack">
                <input
                  type="text"
                  className="grf-input"
                  value={principal}
                  onChange={(e) =>
                    setPrincipal(e.target.value.replace(/[^\d.]/g, ""))
                  }
                />
                {principal && (
                  <span className="grf-amount-label">
                    {convertToWords(principal)}
                  </span>
                )}
              </div>
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Weight</label>
              <input
                type="text"
                className="grf-input"
                value={weight}
                onChange={(e) =>
                  setWeight(e.target.value.replace(/[^\d.]/g, ""))
                }
              />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">No. of Items</label>
              <input type="text" className="grf-input" value={itemsCount} readOnly />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Valuation</label>
              <div className="grf-inline">
                <input type="text" className="grf-input" value={valuation} readOnly />
                <button type="button" className="grf-open" onClick={() => setIsModalOpen(true)}>
                  Open
                </button>
              </div>
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Grant Date</label>
              <input type="text" className="grf-input" value={grantDate} readOnly />
            </div>

            <div className="grf-field col-3">
              <label className="grf-label">Expiry Date</label>
              <input type="text" className="grf-input" value={expiryDate} readOnly />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="grf-footer-nosticky">
          <button
            type="button"
            className="grf-btn grf-btn--ghost"
            onClick={() => navigate("/dashboard")}
          >
            Back
          </button>
          <button type="button" className="grf-btn grf-btn--primary" onClick={handleSubmit}>
            Next
          </button>
        </div>

        <GoldValuationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUpdate={(newWeight, newValuation, details) => {
            setWeight(sanitizeNumber(newWeight));
            setValuation(Number(newValuation).toFixed(2));

            // Save all details (per karat quantities and valAdd)
            localStorage.setItem("pawnGoldDetails", JSON.stringify(details));
          }}
        />
      </div>
    </div>
  );
}